'use client'
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import dayjs from 'dayjs';
import { noto_sans, noto_serif } from '@/globalConstants';
import Link from 'next/link';
import BlogCard from '@/components/EngagementContent/BlogCard';
import BlogContent from '@/components/EngagementContent/BlogContent';
import { Article } from '@/globalTypes';
import { getResponsiveImageUrl } from '@/lib/globalUtils';
import { useAppContext } from '@/contexts/AppContext';
import { tagToPathSegment } from '@/lib/tagSlug';
import { articleLanguage, articlePath } from '@/lib/articleLanguage';

// The article's own text comes from the CMS; these are the few words around it.
const LABELS = {
    en: { updated: 'Updated', minutes: 'min', related: 'Related Articles' },
    hi: { updated: 'अपडेट', minutes: ' मिनट', related: 'संबंधित लेख' },
};

// "30 Oct 2026", or "30 अक्टूबर 2026" on a Hindi article. Pinned to IST so the
// UTC server and the reader's browser print the same day.
const HINDI_DATE = new Intl.DateTimeFormat('hi-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });
const formatDate = (date: string, hindi: boolean) =>
    hindi ? HINDI_DATE.format(new Date(date)) : dayjs(date).format("DD-MMM-YYYY").replaceAll('-', " ");

const BlogView = ({ initialArticle }: { initialArticle: Article }) => {

    const { isAppView } = useAppContext();
    const article = initialArticle;
    const hindi = articleLanguage(article) === 'hi';
    const labels = LABELS[hindi ? 'hi' : 'en'];

    // getResponsiveImageUrl reads window.innerWidth, which is unavailable during SSR
    // and on the first client render. Use an SSR-safe fallback until mounted so the
    // server HTML matches the first client render (no hydration mismatch), then
    // upgrade to the responsive image.
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    const mainImageUrl = isMounted
        ? getResponsiveImageUrl(article)
        : (article.banner?.thumbnail?.url || '');

    return (
        <div className="w-full pt-4 pb-10 md:pt-6 md:pb-12 px-4 md:px-0">
            {/* No min-height here: with justify-center it parked short articles in the
                middle of a screen-tall box, leaving blank space above and below. */}
            <div className={`flex flex-col items-center w-full md:w-[90%] 2xl:w-[70%] mx-auto ${isAppView ? 'bg-white py-6 px-5 rounded-2xl lg:bg-[transparent]' : ''}`}>
                <h1 className={`${noto_serif.className} font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:text-center text-[#1D4749] w-full`}>{article.heading}</h1>

                {/* Article Summary */}
                <p className={`text-md lg:text-lg text-gray-700 mb-5 md:text-center font-[500] w-full ${noto_sans.className}`}>
                    {article.tagline}
                </p>

                {/* Author Information */}
                <div className="flex items-center mb-5 w-full md:justify-center">
                    {article.authorImage && <div className="flex-shrink-0">
                        <Image src={process.env.NEXT_PUBLIC_ASSETS_BASE_URL_CONTENT + `${article.authorImage}`} alt={article.authorName || 'Author'} width={50} height={50} className="rounded-full" quality={20} />
                    </div>}
                    <div className="ml-4 grid gap-[4px]">
                        <p className={`font-semibold text-[#565151] ${noto_sans.className} font-[500] text-[14px]`} >{article.authorName}</p>
                        <p className={`font-[500] text-[#8F8B8B] ${noto_sans.className}  text-[12px] flex  items-center gap-[4px]`}>
                            {`${formatDate(article.publishDate, hindi)} `}
                            {/* Shown when the article changed after publishing (same date as dateModified in the schema). */}
                            {(article as any).updatedAt && dayjs((article as any).updatedAt).isAfter(dayjs(article.publishDate).add(1, 'day'))
                                ? <>• {`${labels.updated} ${formatDate((article as any).updatedAt, hindi)} `}</>
                                : null}
                            •
                            {` ${article.readTime}${labels.minutes}`}
                        </p>
                    </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap mb-6 w-full md:justify-center gap-3">
                    {article.tags.map((tag, index) => {

                        return <Link key={`${tag}-${index}`} href={`/articles/tags/${tagToPathSegment(tag)}`} className='transition ease-in-out delay-150  hover:scale-[120%]  duration-300'>
                            <span className={`px-3 py-1 bg-gray-200 text-sm font-[500] rounded-[0.5rem] text-[#EE9322] ${noto_sans.className}`}>{tag}</span>
                        </Link>
                    })
                    }
                </div>

                {/* Main Image — sized by aspect ratio rather than a fixed 480px box, which
                    on a phone letterboxed a ~200px banner inside ~280px of blur. */}
                <div className="relative w-full max-w-[880px] aspect-[16/9] mx-auto overflow-hidden rounded-lg">
                    <div
                        className="absolute inset-0 bg-center bg-cover filter blur-lg"
                        style={{ backgroundImage: `url(${article.banner ? article.banner.thumbnail.url : ''})` }}
                    />
                    {mainImageUrl && (
                        <Image
                            src={mainImageUrl}
                            alt={article.heading}
                            fill
                            sizes="(max-width: 880px) 100vw, 880px"
                            priority
                            className="z-10 object-cover"
                        />
                    )}
                </div>

                {/* Article Content — block flow (not grid) so adjacent block margins
                    collapse instead of stacking; same reading width as the image. */}
                <div className={`mt-6 mb-4 ${noto_sans.className} text-[#1D4749] font-[500] w-full max-w-[880px] leading-relaxed`}>
                    {article.content.map((contentBlock, index) => {
                        return <BlogContent key={index} contentBlock={contentBlock} index={index} />
                    })
                    }
                </div>


                {/* Related Articles */}
                <div className={`${article.relatedBlogs.length ? '' : 'hidden'} w-full`}>
                    {<div className="mt-8 lg:mt-10">
                        <div className={`__apps__heading flex gap-[8px] justify-center pb-5 lg:pb-6`}>
                            <Image src="/union.svg" alt="union icon" width="30" height="6" />
                            <h2 className={`text-2xl md:text-3xl font-semibold text-center ${noto_serif.className} text-[#1D4749]`}>{labels.related}</h2>
                            <Image src="/union.svg" alt="union icon" width="30" height="6" className='rotate-180' />
                        </div>
                        <div className="flex flex-wrap justify-center gap-6">
                            {
                                article.relatedBlogs.map((relatedBlog: any, index: number) => {
                                    return (
                                        <Link key={relatedBlog.slug} href={articlePath(relatedBlog)} className='block  h-auto'>
                                            <BlogCard thumbnailUrl={relatedBlog.banner.thumbnail.url} tags={relatedBlog.tags} heading={relatedBlog.heading} summary={relatedBlog.tagline} publishDate={relatedBlog.publishDate} readTime={relatedBlog.readTime} orientation={'grid'} slug={article.relatedBlogs[index]} />
                                        </Link>
                                    )
                                })
                            }
                        </div>
                    </div>}
                    {/* Call to Action */}
                    {!isAppView && <div className="mt-8 text-center">
                        <a href='/articles' className="px-8 py-3 bg-orange-600 text-white rounded-full font-semibold text-lg hover:bg-orange-500">View All Articles</a>
                    </div>}
                    {isAppView && <div className="mt-8 text-center">
                        <a href='/articles?source=webview' className="px-8 py-3 bg-orange-600 text-white rounded-full font-semibold text-lg hover:bg-orange-500">View All Articles</a>
                    </div>}
                </div>
            </div>
        </div >
    );
};

export default BlogView;
